import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import AuthModal from '../auth/AuthModal';
import logo from '../../assets/images/bslogo.jpeg';
import {
  buildCategoryTree,
  categoryIdFromLink,
  categoryPath,
  firstNameOf,
  loadCategories,
  loadEvents,
  loadHomepageAssets,
  loadHomepageCopy,
  selectHomepageContent,
  tradeStatusFor,
  upcomingEvents,
} from '../../services/storefront';
import { HOMEPAGE_COPY_DEFAULTS, resolveHomepageCopy } from '../storefront/homepageCopy';
import { STOREFRONT_EVENTS } from '../storefront/storefrontEvents';
import { IconBag, IconChevronDown, IconMenu, IconSearch, IconUser } from '../storefront/icons';
import AnnouncementBar from './nav/AnnouncementBar';
import { buildAnnouncements } from './nav/announcements';
import MegaMenu from './nav/MegaMenu';
import SearchPalette from './nav/SearchPalette';
import MobileMenu from './nav/MobileMenu';
import AccountMenu from './nav/AccountMenu';
import '../../styles/tradingPost.css';
import './TopNav.css';

const MEGA_OPEN_DELAY = 90;
const MEGA_CLOSE_DELAY = 220;
const PRIMARY_COLLECTION_LINKS = 5;

const initialsOf = (name) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'U';
  const first = parts[0][0];
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return `${first}${last}`.toUpperCase();
};

const isTypingTarget = (target) =>
  target instanceof HTMLElement
  && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName));

const TopNav = () => {
  const { isAuthenticated, isApproved, isAdmin, userProfile, logout } = useAuth();
  const { itemCount, openCart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  const [tree, setTree] = useState({ roots: [], byId: new Map() });
  const [featuredIds, setFeaturedIds] = useState([]);
  const [events, setEvents] = useState([]);
  const [copy, setCopy] = useState(HOMEPAGE_COPY_DEFAULTS);
  const [scrolled, setScrolled] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [activeRootId, setActiveRootId] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [authMode, setAuthMode] = useState(null);

  const openTimer = useRef(null);
  const closeTimer = useRef(null);
  const shopButtonRef = useRef(null);

  // Categories + featured order + events all come from the shared,
  // session-cached storefront loaders (the homepage reuses the same requests).
  useEffect(() => {
    let cancelled = false;
    Promise.all([loadHomepageAssets().catch(() => []), loadCategories()])
      .then(([assets, categories]) => {
        if (cancelled) return;
        const { featuredCategories } = selectHomepageContent(assets);
        setTree(buildCategoryTree(categories, featuredCategories));
        setFeaturedIds(featuredCategories.map((asset) => categoryIdFromLink(asset.link_url)).filter(Boolean));
      })
      .catch((err) => console.error('Error loading navigation categories:', err));
    loadEvents()
      .then((list) => {
        if (!cancelled) setEvents(upcomingEvents(list));
      })
      .catch(() => {});
    loadHomepageCopy().then((overrides) => {
      if (!cancelled) setCopy(resolveHomepageCopy(overrides));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setScrolled(window.scrollY > 12));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const clearMegaTimers = useCallback(() => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
  }, []);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => clearMegaTimers, [clearMegaTimers]);

  useEffect(() => {
    clearMegaTimers();
    setMegaOpen(false);
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search, clearMegaTimers]);

  // "/" or Cmd/Ctrl+K opens search; Escape closes the mega menu.
  useEffect(() => {
    const onKey = (event) => {
      if ((event.key === 'k' || event.key === 'K') && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setSearchOpen(true);
      } else if (event.key === '/' && !isTypingTarget(event.target)) {
        event.preventDefault();
        setSearchOpen(true);
      } else if (event.key === 'Escape' && megaOpen) {
        setMegaOpen(false);
        shopButtonRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [megaOpen]);

  useEffect(() => {
    const onOpenSearch = () => setSearchOpen(true);
    const onOpenAuth = (event) => setAuthMode(event.detail?.mode === 'register' ? 'register' : 'login');
    window.addEventListener(STOREFRONT_EVENTS.openSearch, onOpenSearch);
    window.addEventListener(STOREFRONT_EVENTS.openAuth, onOpenAuth);
    return () => {
      window.removeEventListener(STOREFRONT_EVENTS.openSearch, onOpenSearch);
      window.removeEventListener(STOREFRONT_EVENTS.openAuth, onOpenAuth);
    };
  }, []);

  const { roots, byId } = tree;
  const defaultRootId = activeRootId || roots[0]?.id;

  const featuredNodes = useMemo(
    () => featuredIds.map((id) => byId.get(id)).filter(Boolean),
    [featuredIds, byId],
  );

  // Collection shortcuts that don't fit wrap onto a clipped second line of
  // their row (see .tp-nav__collections); mark those inert so keyboard focus
  // skips links nobody can see.
  const collectionsRef = useRef(null);
  const [visibleLinks, setVisibleLinks] = useState(PRIMARY_COLLECTION_LINKS);

  useEffect(() => {
    const row = collectionsRef.current;
    if (!row || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => {
      const links = Array.from(row.children);
      const firstWrapped = links.findIndex((link) => link.offsetTop > links[0].offsetTop);
      setVisibleLinks(firstWrapped === -1 ? links.length : firstWrapped);
    };
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    document.fonts?.ready.then(measure);
    return () => observer.disconnect();
  }, [featuredNodes]);

  // Hover intent: a short delay before opening so passing the cursor over the
  // bar doesn't flash the panel; once open, switching collections is instant.
  const openMega = (rootId, immediate = false) => {
    clearMegaTimers();
    const show = () => {
      if (rootId) setActiveRootId(rootId);
      setMegaOpen(true);
    };
    if (immediate || megaOpen) show();
    else openTimer.current = setTimeout(show, MEGA_OPEN_DELAY);
  };

  const scheduleMegaClose = () => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setMegaOpen(false), MEGA_CLOSE_DELAY);
  };

  const tradeStatus = tradeStatusFor({ isAuthenticated, isApproved, isAdmin });
  const firstName = firstNameOf(userProfile);
  const initials = initialsOf(userProfile?.name);

  const handleSignOut = async () => {
    await logout();
    setMenuOpen(false);
    navigate('/');
  };

  const announcements = useMemo(
    () => buildAnnouncements({
      events,
      tradeState: tradeStatus.state,
      firstName,
      copy,
      onApply: () => setAuthMode('register'),
    }),
    [events, tradeStatus.state, firstName, copy],
  );

  const headerClass = [
    'tp-nav',
    scrolled && 'is-scrolled',
    megaOpen && 'is-mega-open',
  ].filter(Boolean).join(' ');

  return (
    <>
      <AnnouncementBar messages={announcements} />

      <header className={headerClass} onMouseLeave={scheduleMegaClose}>
        <div className="tp-nav__bar tp-container">
          <button
            type="button"
            className="tp-nav__icon-btn tp-nav__burger"
            aria-label="Open menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}
          >
            <IconMenu size={22} />
          </button>

          <Link to="/" className="tp-nav__brand" aria-label="BS Trading Co. home">
            <img src={logo} alt="" className="tp-nav__logo" width="84" height="41" />
            <span className="tp-nav__wordmark" aria-hidden="true">
              <span className="tp-nav__wordmark-name">BS Trading Co.</span>
              <span className="tp-nav__wordmark-tag">Burleson · Texas</span>
            </span>
          </Link>

          <nav className="tp-nav__primary" aria-label="Primary">
            <button
              ref={shopButtonRef}
              type="button"
              className={`tp-nav__link tp-nav__link--shop${megaOpen ? ' is-active' : ''}`}
              aria-expanded={megaOpen}
              aria-controls="tp-mega"
              onClick={() => {
                // Without categories there's no menu to show; go straight to the catalog.
                if (!roots.length) navigate('/products');
                else if (megaOpen) setMegaOpen(false);
                else openMega(defaultRootId, true);
              }}
              onMouseEnter={() => {
                if (roots.length) openMega(defaultRootId);
              }}
            >
              Shop
              <IconChevronDown size={14} className="tp-nav__chevron" />
            </button>
            <div className="tp-nav__collections" ref={collectionsRef}>
              {featuredNodes.slice(0, PRIMARY_COLLECTION_LINKS).map((node, index) => (
                <Link
                  key={node.id}
                  to={categoryPath(node.id)}
                  className={`tp-nav__link${megaOpen && activeRootId === node.rootId ? ' is-active' : ''}`}
                  inert={index >= visibleLinks}
                  onMouseEnter={() => openMega(node.rootId)}
                >
                  {node.name}
                </Link>
              ))}
            </div>
            <Link to="/wholesale" className="tp-nav__link tp-nav__trade" onMouseEnter={scheduleMegaClose}>
              Trade program
            </Link>
          </nav>

          <div className="tp-nav__actions" onMouseEnter={scheduleMegaClose}>
            <button
              type="button"
              className="tp-nav__search"
              onClick={() => setSearchOpen(true)}
              aria-label="Search the catalog"
              aria-keyshortcuts="/ Control+K Meta+K"
            >
              <IconSearch size={18} />
              <span className="tp-nav__search-label">Search the catalog</span>
              <kbd className="tp-nav__kbd">/</kbd>
            </button>

            {isAuthenticated ? (
              <AccountMenu
                profile={userProfile}
                initials={initials}
                firstName={firstName}
                isAdmin={isAdmin}
                tradeStatus={tradeStatus}
                onSignOut={handleSignOut}
              />
            ) : (
              <button type="button" className="tp-nav__signin" onClick={() => setAuthMode('login')}>
                <IconUser size={18} />
                <span>Sign in</span>
              </button>
            )}

            <button
              type="button"
              className="tp-nav__icon-btn tp-nav__cart"
              onClick={openCart}
              aria-label={`Open cart, ${itemCount} ${itemCount === 1 ? 'item' : 'items'}`}
            >
              <IconBag size={22} />
              {itemCount > 0 && (
                <span className="tp-nav__cart-count" key={itemCount}>
                  {itemCount > 99 ? '99+' : itemCount}
                </span>
              )}
            </button>
          </div>
        </div>

        <MegaMenu
          id="tp-mega"
          open={megaOpen}
          roots={roots}
          activeRootId={defaultRootId}
          onActivate={setActiveRootId}
          onPointerEnter={clearMegaTimers}
        />
      </header>

      {megaOpen && <div className="tp-nav-scrim" onClick={() => setMegaOpen(false)} aria-hidden="true" />}

      <SearchPalette
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        roots={roots}
        popular={featuredNodes}
      />

      <MobileMenu
        open={menuOpen}
        onClose={closeMenu}
        logo={logo}
        roots={roots}
        itemCount={itemCount}
        isAuthenticated={isAuthenticated}
        isAdmin={isAdmin}
        profile={userProfile}
        initials={initials}
        tradeStatus={tradeStatus}
        guestPitch={copy['announce.guest']}
        onSearch={() => {
          setMenuOpen(false);
          setSearchOpen(true);
        }}
        onCart={() => {
          setMenuOpen(false);
          openCart();
        }}
        onSignIn={() => {
          setMenuOpen(false);
          setAuthMode('login');
        }}
        onApply={() => {
          setMenuOpen(false);
          setAuthMode('register');
        }}
        onSignOut={handleSignOut}
      />

      <AuthModal
        isOpen={authMode !== null}
        initialMode={authMode || 'login'}
        onClose={() => setAuthMode(null)}
      />
    </>
  );
};

export default TopNav;
